import axios from 'axios';
import { UserInfo, TokenPayload } from '../types/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 解析JWT token
export const parseJwt = (token: string): TokenPayload | null => {
  try {
    console.log('开始解析token');
    
    // 检查token格式
    const parts = token.split('.');
    if (parts.length < 2) {
      console.error('Token格式不正确，缺少分隔符');
      return null;
    }
    
    const base64Url = parts[1];
    if (!base64Url) return null;
    
    // 处理Base64URL编码
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // 添加填充
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    
    try {
      // 尝试解码
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      
      console.log('解析成功，得到payload');
      return JSON.parse(jsonPayload) as TokenPayload;
    } catch (decodeError) {
      console.error('Base64解码失败:', decodeError);
      
      // 尝试直接解析JSON
      try {
        const directJson = decodeURIComponent(escape(atob(base64)));
        return JSON.parse(directJson) as TokenPayload;
      } catch (directError) {
        console.error('直接解析JSON失败:', directError);
        return null;
      }
    }
  } catch (error) {
    console.error('解析token失败:', error);
    return null;
  }
};

// 验证token是否过期
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
};

// 验证token并获取用户信息
export const validateToken = async (
  token: string
): Promise<{ 
  isValid: boolean; 
  userInfo: UserInfo | null; 
  error?: string;
}> => {
  try {
    console.log('开始验证token');
    
    // 检查token是否为空
    if (!token || token.trim() === '') {
      console.error('Token为空');
      return { 
        isValid: false, 
        userInfo: null, 
        error: '未提供token' 
      };
    }
    
    // 检查token是否符合JWT格式
    if (token.split('.').length < 3) {
      console.error('Token不符合JWT格式');
      return { 
        isValid: false, 
        userInfo: null, 
        error: 'Token格式不正确' 
      };
    }
    
    // 首先检查token是否已过期
    // 注意：我们先解析payload，再检查过期，因为isTokenExpired依赖于parseJwt
    const payload = parseJwt(token);
    if (!payload) {
      console.error('无法解析token的payload');
      return { 
        isValid: false, 
        userInfo: null, 
        error: '无效的token格式' 
      };
    }
    
    // 检查过期时间
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      console.error('Token已过期');
      return { 
        isValid: false, 
        userInfo: null, 
        error: '登录已过期，请重新登录' 
      };
    }

    // 验证token有效性（可选：发送到服务器验证）
    if (API_BASE_URL) {
      try {
        console.log('尝试调用后端验证API');
        const response = await axios.get(`${API_BASE_URL}/auth/validate`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.valid) {
          console.log('后端验证成功');
          // 如果后端返回了完整的用户信息，使用后端返回的
          if (response.data.user) {
            return {
              isValid: true,
              userInfo: response.data.user
            };
          }
        } else {
          console.error('后端验证失败:', response.data);
          return { 
            isValid: false, 
            userInfo: null, 
            error: response.data.message || 'Token验证失败' 
          };
        }
      } catch (apiError) {
        console.warn('Token验证API调用失败，将使用本地解析的token', apiError);
        // API调用失败时，如果token格式正确且未过期，仍然认为有效
      }
    }

    // 检查必要的字段
    if (!payload.userId) {
      console.error('Token缺少必要的userId字段');
      return { 
        isValid: false, 
        userInfo: null, 
        error: 'Token缺少必要的用户信息' 
      };
    }

    // 从token中提取用户信息
    console.log('从token提取用户信息:', payload);
    const userInfo: UserInfo = {
      userId: payload.userId,
      username: payload.username || '未知用户',
      name: payload.name || payload.username || '未知用户',  // 使用name或回退到username
      email: payload.email || '',
      role: payload.role || 'student',
      grade_level: payload.grade_level || '',
      avatar: payload.avatar || '',
      lastLogin: payload.lastLogin || new Date().toISOString()
    };

    console.log('验证成功，用户信息:', userInfo);
    return { 
      isValid: true, 
      userInfo 
    };
  } catch (error) {
    console.error('Token验证失败:', error);
    return { 
      isValid: false, 
      userInfo: null, 
      error: error instanceof Error ? error.message : '验证失败' 
    };
  }
};

// 刷新token
export const refreshToken = async (refreshToken: string) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken
    });
    return {
      success: true,
      token: response.data.token,
      refreshToken: response.data.refreshToken
    };
  } catch (error) {
    console.error('刷新token失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '刷新token失败'
    };
  }
};

// 登出
export const logout = async (token: string) => {
  try {
    await axios.post(
      `${API_BASE_URL}/auth/logout`,
      {},
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    return { success: true };
  } catch (error) {
    console.error('登出失败:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : '登出失败' 
    };
  }
};
